<template>
  <div class="profile-container">
    <!-- Profile Header -->
    <div class="profile-header">
      <div class="profile-info">
        <h2>{{ profile.first_name }} {{ profile.last_name }}</h2>
        <span class="profile-role" :class="`role-${profile.role}`">
          {{ $t(`accountPage.role.${profile.role}`) }}
        </span>
        <p class="profile-email">{{ profile.email }}</p>
      </div>
    </div>

    <!-- Profile Navigation Tabs -->
    <div class="profile-tabs">
      <div 
        v-for="tab in tabs" 
        :key="tab.id" 
        class="profile-tab" 
        :class="{ 'active': activeTab === tab.id }"
        @click="activeTab = tab.id"
      >
        <i :class="tab.icon"></i> {{ $t(tab.title) }}
      </div>
    </div>

    <!-- Profile Content -->
    <div class="profile-content">
      <!-- Personal Information Tab -->
      <div v-if="activeTab === 'personal'" class="profile-tab-content">
        <div class="profile-card">
          <h3>{{ $t('accountPage.profileInformation') }}</h3>
          <form @submit.prevent="updateProfile" class="profile-form">
            <div class="form-row">
              <div class="form-group">
                <label for="first-name">{{ $t('accountPage.firstName') }}</label>
                <input 
                  type="text" 
                  id="first-name" 
                  class="form-control" 
                  v-model="profile.first_name" 
                  required
                >
              </div>
              <div class="form-group">
                <label for="last-name">{{ $t('accountPage.lastName') }}</label>
                <input 
                  type="text" 
                  id="last-name" 
                  class="form-control" 
                  v-model="profile.last_name" 
                  required
                >
              </div>
            </div>
            <div class="form-group">
              <label for="email">{{ $t('accountPage.email') }}</label>
              <input 
                type="email" 
                id="email" 
                class="form-control" 
                v-model="profile.email" 
                required
              >
            </div>
            <div class="form-group">
              <label for="phone">{{ $t('accountPage.phone') }}</label>
              <input 
                type="tel" 
                id="phone" 
                class="form-control" 
                v-model="profile.phone"
              >
            </div>
            <div class="form-actions">
              <button type="submit" class="btn-primary" :disabled="isUpdating">
                <span v-if="isUpdating" class="loading-spinner"></span>
                {{ $t('accountPage.updateProfile') }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Security Tab -->
      <div v-if="activeTab === 'security'" class="profile-tab-content">
        <div class="profile-card">
          <h3>{{ $t('accountPage.changePassword') }}</h3>
          <form @submit.prevent="changePassword" class="profile-form">
            <div class="form-group">
              <label for="current-password">{{ $t('accountPage.currentPassword') }}</label>
              <input 
                type="password" 
                id="current-password" 
                class="form-control" 
                v-model="passwords.current" 
                required
              >
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="new-password">{{ $t('accountPage.newPassword') }}</label>
                <input 
                  type="password" 
                  id="new-password" 
                  class="form-control" 
                  v-model="passwords.new" 
                  required
                >
              </div>
              <div class="form-group">
                <label for="confirm-password">{{ $t('accountPage.confirmNewPassword') }}</label>
                <input 
                  type="password" 
                  id="confirm-password" 
                  class="form-control" 
                  v-model="passwords.confirm" 
                  required
                >
              </div>
            </div>
            <div class="password-strength" v-if="passwords.new">
              <div class="strength-meter" :class="passwordStrength"></div>
              <span class="strength-text">{{ passwordStrengthText }}</span>
            </div>
            <div class="form-actions">
              <button type="submit" class="btn-primary" :disabled="isChangingPassword">
                <span v-if="isChangingPassword" class="loading-spinner"></span>
                {{ $t('accountPage.changePassword') }}
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Stats Tab -->
      <div v-if="activeTab === 'stats'" class="profile-tab-content">
        <div class="stats-grid">
          <div class="stat-card" v-for="stat in userStats" :key="stat.id">
            <div class="stat-icon">
              <i :class="stat.icon"></i>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ stat.value }}</div>
              <div class="stat-label">{{ stat.label }}</div>
            </div>
          </div>
        </div>
        
        <div class="profile-card">
          <h3>{{ $t('accountPage.upcomingLessons') }}</h3>
          <div class="upcoming-lessons" v-if="upcomingLessons.length > 0">
            <div class="lesson-item" v-for="lesson in upcomingLessons" :key="lesson.id">
              <div class="lesson-date">
                <div class="date-day">{{ formatLessonDay(lesson.date) }}</div>
                <div class="date-month">{{ formatLessonMonth(lesson.date) }}</div>
              </div>
              <div class="lesson-details">
                <div class="lesson-title">{{ lesson.title }}</div>
                <div class="lesson-time">
                  {{ formatTime(lesson.start_time) }} - {{ formatTime(lesson.end_time) }}
                </div>
                <div class="lesson-group">{{ lesson.group_name }}</div>
              </div>
            </div>
          </div>
          <div class="empty-state" v-else>
            <i class="fas fa-calendar-check"></i>
            <p>{{ $t('accountPage.noUpcomingLessons') }}</p>
          </div>
        </div>
      </div>

      <!-- Preferences Tab -->
      <div v-if="activeTab === 'preferences'" class="profile-tab-content">
        <div class="profile-card">
          <h3>{{ $t('accountPage.accountPreferences') }}</h3>
          <div class="preferences-section">
            <div class="preference-item">
              <div class="preference-label">
                <h4>{{ $t('accountPage.language') }}</h4>
                <p>{{ $t('accountPage.languageDescription') }}</p>
              </div>
              <div class="preference-control">
                <select v-model="selectedLanguage" @change="updateLanguage" class="form-control">
                  <option value="en">English</option>
                  <option value="uk">Українська</option>
                  <option value="pl">Polski</option>
                </select>
              </div>
            </div>
            
            <div class="preference-item">
              <div class="preference-label">
                <h4>{{ $t('accountPage.notifications') }}</h4>
                <p>{{ $t('accountPage.notificationsDescription') }}</p>
              </div>
              <div class="preference-control">
                <div class="toggle-switch">
                  <input type="checkbox" id="email-notifications" v-model="preferences.emailNotifications">
                  <label for="email-notifications"></label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Toast Notification -->
    <div class="toast-notification" v-if="notification.visible">
      <i :class="notification.icon"></i>
      <span class="toast-message">{{ notification.message }}</span>
    </div>
  </div>
</template>
 
<script>
import axiosInstance from '../router/axiosSetup';

export default {
  data() {
    return {
      profile: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        username: '',
        role: '',
      },
      passwords: {
        current: '',
        new: '',
        confirm: ''
      },
      activeTab: 'personal',
      isSaving: false,
      notification: {
        visible: false,
        message: '',
        type: 'success'
      },
      passwordStrength: 0,
      showDeleteAccountModal: false,
      deleteAccountConfirmation: '',
      isUpdating: false,      
      isChangingPassword: false,
      tabs: [
        { id: 'personal', title: 'accountPage.personalInfo', icon: 'fas fa-user' },
        { id: 'security', title: 'accountPage.security', icon: 'fas fa-shield-alt' }
      ]
    };
  },
  
  computed: {
    availableTabs() {
      return this.tabs.map(tab => ({
        ...tab,
        label: this.$t(tab.title)
      }));
    },
    passwordMismatch() {
      return this.passwords.new !== this.passwords.confirm && this.passwords.confirm.length > 0;
    },
    
    passwordStrengthText() {
      if (this.passwordStrength <= 1) return this.$t('accountPage.passwordWeak');
      if (this.passwordStrength <= 3) return this.$t('accountPage.passwordMedium');
      return this.$t('accountPage.passwordStrong');
    },
    
    passwordStrengthClass() {
      if (this.passwordStrength <= 1) return 'weak';
      if (this.passwordStrength <= 3) return 'medium';
      return 'strong';
    },
    
    profileImageUrl() {
      return this.profile.profile_image 
        ? `${process.env.VUE_APP_API_URL}/uploads/profile/${this.profile.profile_image}` 
        : '/images/default-avatar.png';
    }
  },
  
  watch: {
    'passwords.new'(newVal) {
      this.checkPasswordStrength(newVal);
    }
  },
  
  methods: {
    async fetchProfile() {
      try {
        const response = await axiosInstance.get('/user/profile', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.profile = { ...this.profile, ...response.data };
      } catch (error) {
        this.showNotification(this.$t('accountPage.errorFetchingProfile'), 'error');
        console.error('Error fetching profile:', error);
      }
    },
    
    async updateProfile() {
      try {
        this.isSaving = true;
        await axiosInstance.put('/user/update-profile', this.profile, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        this.showNotification(this.$t('accountPage.profileUpdated'), 'success');
        
        // Update stored username if it was changed
        if (this.profile.username) {
          localStorage.setItem('username', this.profile.username);
        }
      } catch (error) {
        this.showNotification(this.$t('accountPage.errorUpdatingProfile'), 'error');
        console.error('Error updating profile:', error);
      } finally {
        this.isSaving = false;
      }
    },
    
    async changePassword() {
      if (this.passwords.new !== this.passwords.confirm) {
        this.showNotification(this.$t('accountPage.passwordsDontMatch'), 'error');
        return;
      }
      
      if (this.passwordStrength < 3) {
        this.showNotification(this.$t('accountPage.passwordTooWeak'), 'error');
        return;
      }
      
      try {
        this.isSaving = true;
        await axiosInstance.put('/user/change-password', {
          current_password: this.passwords.current,
          new_password: this.passwords.new
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        // Reset password fields
        this.passwords = {
          current: '',
          new: '',
          confirm: ''
        };
        
        this.showNotification(this.$t('accountPage.passwordChanged'), 'success');
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.showNotification(this.$t('accountPage.currentPasswordIncorrect'), 'error');
        } else {
          this.showNotification(this.$t('accountPage.errorChangingPassword'), 'error');
        }
        console.error('Error changing password:', error);
      } finally {
        this.isSaving = false;
      }
    },
    
    checkPasswordStrength(password) {
      // Simple password strength calculator
      let strength = 0;
      
      // Length check
      if (password.length >= 8) strength++;
      
      // Contains lowercase
      if (/[a-z]/.test(password)) strength++;
      
      // Contains uppercase
      if (/[A-Z]/.test(password)) strength++;
      
      // Contains number
      if (/[0-9]/.test(password)) strength++;
      
      // Contains special character
      if (/[^a-zA-Z0-9]/.test(password)) strength++;
      
      this.passwordStrength = strength;
    },
    
    showNotification(message, type = 'success') {
      this.notification = {
        visible: true,
        message,
        type
      };
      
      // Auto-hide after 3 seconds
      setTimeout(() => {
        this.notification.visible = false;
      }, 3000);
    },
    
    async deleteAccount() {
      if (this.deleteAccountConfirmation !== this.profile.username) {
        this.showNotification(this.$t('accountPage.usernameConfirmationIncorrect'), 'error');
        return;
      }
      
      try {
        await axiosInstance.delete('/user/delete-account', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        // Clear local storage and redirect to login
        localStorage.clear();
        this.$router.push('/login');
      } catch (error) {
        this.showNotification(this.$t('accountPage.errorDeletingAccount'), 'error');
        console.error('Error deleting account:', error);
      }
    }
  },
  
  created() {
    // Fetch profile information on component creation
    this.fetchProfile();
  }
};
</script>
  
<style scoped>
.profile-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

/* Profile Header */
.profile-header {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.profile-avatar-section {
  margin-right: 2rem;
}

.profile-avatar {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: #e2e8f0;
  overflow: hidden;
  cursor: pointer;
}

.profile-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 600;
  color: #4b5563;
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  opacity: 0;
  transition: opacity 0.2s;
}

.profile-avatar:hover .avatar-overlay {
  opacity: 1;
}

.profile-info {
  flex: 1;
}

.profile-info h2 {
  margin: 0 0 0.5rem 0;
  font-size: 1.75rem;
  font-weight: 600;
}

.profile-role {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 20px;
  margin-bottom: 0.5rem;
}

.role-student {
  background-color: #dbeafe;
  color: #1e40af;
}

.role-teacher {
  background-color: #dcfce7;
  color: #15803d;
}

.role-admin {
  background-color: #fce7f3;
  color: #9d174d;
}

.profile-email {
  margin: 0;
  color: #6b7280;
  font-size: 0.95rem;
}

/* Tabs Navigation */
.profile-tabs {
  display: flex;
  margin-bottom: 1.5rem;
  overflow-x: auto;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  border-bottom: 1px solid #e5e7eb;
}

.profile-tabs::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.profile-tab {
  padding: 1rem 1.5rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  border-bottom: 2px solid transparent;
}

.profile-tab i {
  margin-right: 0.5rem;
  font-size: 0.9rem;
}

.profile-tab:hover {
  color: #4b5563;
}

.profile-tab.active {
  color: #4f46e5;
  border-bottom-color: #4f46e5;
}

/* Content Area */
.profile-tab-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.profile-card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.profile-card h3 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  color: #1f2937;
}

/* Forms */
.profile-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.form-group {
  flex: 1;
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #4b5563;
  font-size: 0.9rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.form-control:focus {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.25);
  outline: none;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn-primary {
  background-color: #4f46e5;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary:hover {
  background-color: #4338ca;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(79, 70, 229, 0.25);
}

.btn-primary:disabled {
  background-color: #a5b4fc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Password Strength */
.password-strength {
  margin-bottom: 1rem;
}

.strength-meter {
  height: 4px;
  border-radius: 2px;
  margin-bottom: 0.5rem;
}

.strength-meter.weak {
  background-color: #ef4444;
  width: 25%;
}

.strength-meter.fair {
  background-color: #f97316;
  width: 50%;
}

.strength-meter.good {
  background-color: #10b981;
  width: 75%;
}

.strength-meter.strong {
  background-color: #059669;
  width: 100%;
}

.strength-text {
  font-size: 0.8rem;
  color: #6b7280;
}

/* Activity List */
.activity-list {
  display: flex;
  flex-direction: column;
}

.activity-item {
  display: flex;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.activity-icon.login {
  background-color: #e0f2fe;
  color: #0369a1;
}

.activity-icon.password {
  background-color: #ffedd5;
  color: #9a3412;
}

.activity-details {
  flex: 1;
}

.activity-title {
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.activity-meta {
  display: flex;
  font-size: 0.85rem;
  color: #6b7280;
}

.activity-time {
  margin-right: 1rem;
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 1.25rem;
  display: flex;
  align-items: center;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-right: 1rem;
  color: #4f46e5;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
}

.stat-label {
  font-size: 0.85rem;
  color: #6b7280;
}

/* Upcoming Lessons */
.upcoming-lessons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.lesson-item {
  display: flex;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #f3f4f6;
  transition: all 0.2s;
}

.lesson-item:hover {
  background-color: #f9fafb;
  transform: translateY(-1px);
}

.lesson-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  min-width: 60px;
  text-align: center;
}

.date-day {
  font-size: 1.5rem;
  font-weight: 600;
  color: #4f46e5;
}

.date-month {
  font-size: 0.8rem;
  color: #6b7280;
  text-transform: uppercase;
}

.lesson-details {
  flex: 1;
}

.lesson-title {
  font-weight: 500;
  margin-bottom: 0.25rem;
}

.lesson-time {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.lesson-group {
  font-size: 0.85rem;
  color: #4b5563;
  font-weight: 500;
}

/* Preferences */
.preferences-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.preference-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.preference-item:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.preference-label {
  flex: 1;
  margin-right: 2rem;
}

.preference-label h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 500;
}

.preference-label p {
  margin: 0;
  font-size: 0.9rem;
  color: #6b7280;
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch label {
  display: block;
  width: 60px;
  height: 30px;
  background-color: #e5e7eb;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
}

.toggle-switch label:after {
  content: "";
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: white;
  top: 3px;
  left: 3px;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.toggle-switch input:checked + label {
  background-color: #4f46e5;
}

.toggle-switch input:checked + label:after {
  transform: translateX(30px);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 2rem 0;
  color: #9ca3af;
}

.empty-state i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.empty-state p {
  font-size: 1.1rem;
  margin: 0;
}

/* Toast Notification */
.toast-notification {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 12px 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  min-width: 300px;
  z-index: 1050;
  animation: slideInRight 0.3s, fadeOut 0.3s 2.7s forwards;
}

.toast-notification i {
  font-size: 1.25rem;
  margin-right: 12px;
  color: #4f46e5;
}

.toast-message {
  font-size: 0.95rem;
  color: #1f2937;
}

@keyframes slideInRight {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* Loading Spinner */
.loading-spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .profile-header {
    flex-direction: column;
    text-align: center;
    padding: 1.5rem 1rem;
  }
  
  .profile-avatar-section {
    margin-right: 0;
    margin-bottom: 1.5rem;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .preference-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .preference-label {
    margin-right: 0;
    margin-bottom: 1rem;
  }
  
  .profile-tabs {
    padding-bottom: 0.5rem;
  }
  
  .profile-tab {
    padding: 0.75rem 1rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>